import React from 'react';
import { Alert, Box, Button, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ITeacherOpenResponseAttempt, VerbaScholaApi } from '../api/VerbaScholaApi';
import { ReviewRow } from '../component/VerbaScholaTeacherOpenResponseReview';
import { BLUE, BORDER, INTER, NAVY, POPPINS, SLATE, SOFT_BLUE, SUCCESS_SOFT, SUCCESS_TEXT } from '../style/verbaScholaPalette';

interface ReviewGroup {
    key: string;
    attempts: ITeacherOpenResponseAttempt[];
    latest: ITeacherOpenResponseAttempt;
    final?: ITeacherOpenResponseAttempt;
}

function groupAttempts(attempts: ITeacherOpenResponseAttempt[]): ReviewGroup[] {
    const grouped = new Map<string, ITeacherOpenResponseAttempt[]>();
    attempts.forEach(attempt => {
        const key = `${attempt.student_user_id}:${attempt.step_id}`;
        grouped.set(key, [...(grouped.get(key) ?? []), attempt]);
    });
    return Array.from(grouped, ([key, rows]) => {
        rows.sort((a, b) => a.attempt_number - b.attempt_number);
        return { key, attempts: rows, latest: rows[rows.length - 1], final: rows.find(row => row.is_final) };
    }).sort((a, b) => Date.parse(b.latest.submitted_at) - Date.parse(a.latest.submitted_at));
}

export default function VerbaScholaTeacherLessonOpenResponseReviewPage({ lessonId }: { lessonId: number }) {
    const [attempts, setAttempts] = React.useState<ITeacherOpenResponseAttempt[]>([]);
    const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
    const [filter, setFilter] = React.useState<'awaiting' | 'all'>('awaiting');
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    // Sending a review can remove the learner from Needs review; say so and keep focus on the page.
    const [notice, setNotice] = React.useState('');
    const noticeRef = React.useRef<HTMLDivElement | null>(null);
    React.useEffect(() => { if (notice) noticeRef.current?.focus(); }, [notice]);
    const refresh = React.useCallback(async () => {
        const response = await VerbaScholaApi.getTeacherLessonOpenResponses(lessonId);
        setAttempts(response.data.attempts);
        setError('');
    }, [lessonId]);
    React.useEffect(() => {
        setLoading(true);
        void refresh().catch(() => setError('Could not load open answers.')).finally(() => setLoading(false));
    }, [refresh]);
    const [refreshing, setRefreshing] = React.useState(false);
    const reload = () => {
        setRefreshing(true);
        void refresh().catch(() => setError('Could not load open answers.')).finally(() => setRefreshing(false));
    };
    // AI feedback arrives after submission. While a check is queued or running, look again every
    // 15 seconds for up to 20 minutes (Refresh still works after that). Each saved answer keeps its
    // unsaved grade and notes across a refresh (ReviewRow is keyed by attempt).
    const checking = attempts.some(attempt => attempt.assessment_status === 'queued' || attempt.assessment_status === 'processing');
    React.useEffect(() => {
        if (!checking) return;
        let polls = 0;
        const timer = window.setInterval(() => {
            if (++polls > 80) window.clearInterval(timer);
            else if (document.visibilityState === 'visible') void refresh().catch(() => undefined);
        }, 15000);
        return () => window.clearInterval(timer);
    }, [checking, refresh]);

    const groups = groupAttempts(attempts);
    const pending = groups.filter(group => group.final && !group.final.feedback_released_at).length;
    const visible = filter === 'awaiting' ? groups.filter(group => group.final && !group.final.feedback_released_at) : groups;
    const selected = visible.find(group => group.key === selectedKey) ?? visible[0];
    // Stay on the learner shown, even before the teacher picks one: a refresh can list a newer
    // submission first, and only the selected learner's reviews are mounted, so following the
    // first row would discard an unsaved grade. The selection moves on only when that learner
    // leaves the list (their feedback was sent from Needs review, here or by another teacher,
    // whose save this draft could no longer overwrite anyway).
    const selectedGroupKey = selected?.key ?? null;
    React.useEffect(() => {
        if (selectedGroupKey !== null && selectedGroupKey !== selectedKey) setSelectedKey(selectedGroupKey);
    }, [selectedGroupKey, selectedKey]);

    if (loading) return <Typography role="status" sx={{ color: SLATE }}>Loading open answers…</Typography>;
    return <Stack spacing={2}>
        {error && <Alert severity="error" action={<Button onClick={() => void refresh().catch(() => setError('Could not load open answers.'))}>Retry</Button>}>{error}</Alert>}
        {notice && <Alert severity="success" role="status" ref={noticeRef} tabIndex={-1} onClose={() => setNotice('')}
            sx={{ '&:focus-visible': { outline: `3px solid ${BLUE}`, outlineOffset: 2 } }}>{notice}</Alert>}
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
            <Box>
                <Typography sx={{ fontFamily: POPPINS, fontSize: 20, fontWeight: 700, color: NAVY }}>Open answer review</Typography>
                <Typography sx={{ color: SLATE, fontSize: 13 }}>Review writing, speaking, and writing with speaking. Compare each learner's saved revisions with their final answer.</Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={`${pending} awaiting review`} sx={{ backgroundColor: pending ? SOFT_BLUE : SUCCESS_SOFT, color: pending ? BLUE : SUCCESS_TEXT, fontWeight: 700 }} />
                <Button onClick={reload} disabled={refreshing} sx={{ minHeight: 44, color: BLUE, textTransform: 'none', fontFamily: INTER, fontWeight: 700 }}>
                    {refreshing ? 'Refreshing…' : 'Refresh'}</Button>
            </Stack>
        </Stack>
        <ToggleButtonGroup exclusive size="small" value={filter} aria-label="Filter open answers"
            onChange={(_, value: 'awaiting' | 'all' | null) => { if (value) setFilter(value); }}
            sx={{ alignSelf: 'flex-start', '& .MuiToggleButton-root': { textTransform: 'none', color: NAVY, minHeight: 44 },
                '& .Mui-selected': { backgroundColor: `${SOFT_BLUE} !important`, color: `${BLUE} !important` } }}>
            <ToggleButton value="awaiting">Needs review ({pending})</ToggleButton>
            <ToggleButton value="all">All answers ({groups.length})</ToggleButton>
        </ToggleButtonGroup>
        {!visible.length ? <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, p: 3, backgroundColor: '#fff' }}>
            <Typography sx={{ color: SLATE }}>{groups.length ? 'No final answers need review right now. Select All answers to view saved work.' : 'No open answers have been submitted for this lesson.'}</Typography>
        </Box> : <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '300px minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, backgroundColor: '#fff', overflow: 'hidden' }}>
                <Typography sx={{ px: 2, py: 1.5, color: NAVY, fontFamily: POPPINS, fontWeight: 700 }}>Students and exercises</Typography>
                {visible.map(group => <Button key={group.key} fullWidth onClick={() => setSelectedKey(group.key)} aria-pressed={group.key === selected?.key}
                    sx={{ display: 'block', textAlign: 'left', textTransform: 'none', borderRadius: 0, px: 2, py: 1.5,
                        borderTop: `1px solid ${BORDER}`, backgroundColor: group.key === selected?.key ? SOFT_BLUE : '#fff' }}>
                    <Typography sx={{ color: NAVY, fontFamily: INTER, fontWeight: 700, fontSize: 13 }}>{group.latest.student_name || `Student ${group.latest.student_user_id}`}</Typography>
                    <Typography sx={{ color: SLATE, fontSize: 12 }}>{group.latest.exercise_title || group.latest.step_title || 'Open answer'}</Typography>
                    <Typography sx={{ color: SLATE, fontSize: 11.5 }}>{group.attempts.length} saved {group.attempts.length === 1 ? 'answer' : 'answers'} · {group.final ? group.final.feedback_released_at ? 'Sent' : 'Needs review' : 'In progress'}</Typography>
                </Button>)}
            </Box>
            {selected && <Stack spacing={2}>
                <Typography sx={{ color: NAVY, fontFamily: POPPINS, fontWeight: 700, fontSize: 16 }}>Saved answers in order</Typography>
                {selected.attempts.map(attempt => <ReviewRow key={attempt.id} stepId={attempt.step_id!} attempt={attempt} onSaved={async released => {
                    await refresh();
                    if (released) setNotice(`Feedback sent to ${attempt.student_name || `Student ${attempt.student_user_id}`}.`
                        + (filter === 'awaiting' ? ' They are no longer in Needs review.' : ''));
                }} />)}
            </Stack>}
        </Box>}
    </Stack>;
}
